import React, { useCallback } from "react";
import styled from "styled-components";

import StarterTemplatesPaneController, { StarterTemplatesPaneControllerState } from "js/controllers/StarterTemplatesPaneController";
import { NoMatchNotice } from "js/react/components/Notice";
import Spinner from "js/react/components/Spinner";
import { UIPaneResultsContainer } from "js/react/views/AddSlide/Panes/Components/SearchBox";

import { StarterTemplateItem } from "./StarterTemplateItem";

export interface StarterTemplateListProps {
    selectedId: string | null;
    onSelect: (record: StarterTemplatesPaneControllerState["data"][number]) => void;
    onCreate: (presentationId: string) => void;
    onCategoryClick?: (category: string) => void;
}

const ListContainer = styled(UIPaneResultsContainer)`
    margin-top: 0;
    padding: 0;
    flex: 1;
    min-width: 0;
    min-height: 0;
    box-sizing: border-box;
    overflow-x: hidden;
    overflow-y: auto;
`;

const Container = styled.div`
    width: 100%;
    min-width: 0;
`;

function StarterTemplateList({
    selectedId,
    onSelect,
    onCreate,
    onCategoryClick,
    data,
    filters,
    search,
    fetching,
}: StarterTemplateListProps & StarterTemplatesPaneControllerState) {
    const handleVisibleChange = useCallback((presentationId: string, isVisible: boolean) => {
        if (!isVisible || fetching || !StarterTemplatesPaneController.canLoadMore) {
            return;
        }

        const index = data.findIndex(record => record.document.id === presentationId);
        if (index === data.length - 1) {
            void StarterTemplatesPaneController.loadMore();
        }
    }, [data, fetching]);

    const hasFilter = filters.length > 0 || !!search.query;
    const hasNoMatch = data.length === 0 && hasFilter;
    const isLoadingInitial = fetching && data.length === 0;

    if (isLoadingInitial || !StarterTemplatesPaneController.initialized) {
        return <Spinner />;
    }

    if (hasNoMatch) {
        return (
            <ListContainer>
                <NoMatchNotice />
            </ListContainer>
        );
    }

    return (
        <ListContainer>
            <Container>
                {data.map(record => (
                    <StarterTemplateItem
                        key={record.document.id}
                        record={record}
                        selected={selectedId === record.document.id}
                        onSelect={() => onSelect(record)}
                        onCreate={onCreate}
                        onCategoryClick={onCategoryClick}
                        onVisibleChange={isVisible => handleVisibleChange(record.document.id, isVisible)}
                    />
                ))}
            </Container>
        </ListContainer>
    );
}

export default StarterTemplatesPaneController.withState(StarterTemplateList);
